/**
 * Enfileiramento de eventos e limites da fila.
 */

import {
  sendEvent,
  sendEventBeaconOnly,
  type SendEventParams,
} from '../../utils/sendEvent';
import type { PixelResponse } from '../../types';
import { shouldTrack } from '../consent';
import { shouldDedupeEvent } from '../dedupe';
import { validateEventParams } from '../validateEvent';
import { setQueuePressureLevel } from '../backpressure';
import { nextSessionSequence } from '../ordering/sessionSequence';
import { enrichCausality, noteConversionEvent } from '../ordering/causality';
import {
  computeMemoryPressure,
  recordEnqueueForPressure,
} from '../performance/memoryPressure';
import { evictLowPriorityItems } from '../storage/quotaManager';
import {
  recordQueueSize,
  recordEventPhase,
  recordDropped,
  recordDuplicated,
  recordEnqueueStorm,
  recordOwnershipDenied,
} from '../telemetry';
import { isLeaderTab } from '../tabCoordinator';
import {
  acquireOperationContext,
  assertOperationValid,
} from '../coordination/ownershipGate';
import {
  DROPPABLE_BEHAVIOR_TYPES,
  MAX_EVENT_BYTES,
  eventTypeToQueueType,
  type QueueItem,
  type QueuePriority,
} from './types';
import {
  getMaxQueueItemsLimit,
  getQueueLength,
  getQueueMutable,
  persistQueue,
  pushQueueItem,
  removeQueueItemById,
  resyncQueueIndexFromArray,
} from './queueState';

export function sendPageViewImmediately(
  params: SendEventParams
): Promise<PixelResponse> {
  if (!shouldTrack()) {
    return Promise.resolve({
      success: false,
      error: 'tracking disabled',
      timestamp: Date.now(),
    });
  }
  return sendEvent(params);
}

/**
 * Envio síncrono via sendBeacon para sobreviver a redirect imediato.
 * Não entra na fila — evita ficar só em memória até o usuário voltar.
 */
export function sendEventImmediately(
  params: SendEventParams
): Promise<PixelResponse> {
  if (!shouldTrack()) {
    return Promise.resolve({
      success: false,
      error: 'tracking disabled',
      timestamp: Date.now(),
    });
  }
  if (
    typeof navigator !== 'undefined' &&
    typeof navigator.sendBeacon === 'function'
  ) {
    sendEventBeaconOnly(params);
    return Promise.resolve({
      success: true,
      event_id: `beacon_${Date.now()}`,
      timestamp: Date.now(),
    });
  }
  return sendEvent(params);
}

function applyBackpressure(): void {
  const limit = getMaxQueueItemsLimit();
  const size = getQueueLength();
  if (size > limit * 0.9) setQueuePressureLevel(3);
  else if (size > limit * 0.7) setQueuePressureLevel(2);
  else if (size > limit * 0.5) setQueuePressureLevel(1);
  else setQueuePressureLevel(0);
}

function enforceQueueLimits(incoming: QueueItem): boolean {
  applyBackpressure();
  const limit = getMaxQueueItemsLimit();
  const fillRatio = getQueueLength() / Math.max(1, limit);
  // Sob pressão extrema: mutação de fila só com ownership válido (não-líder descarta).
  if (fillRatio > 0.8) {
    const ctx = acquireOperationContext('queue_mutate');
    if (!isLeaderTab() || !assertOperationValid(ctx)) {
      recordOwnershipDenied();
      recordDropped(1);
      return false;
    }
  }
  const queue = getQueueMutable();
  const pressure = computeMemoryPressure(queue, limit);
  if (pressure.level > 0.85) {
    const evicted = evictLowPriorityItems([...queue], limit);
    queue.length = 0;
    queue.push(...evicted);
    resyncQueueIndexFromArray();
  }
  if (queue.length < limit) return true;
  if (DROPPABLE_BEHAVIOR_TYPES.has(incoming.params.type)) {
    recordDropped(1);
    return false;
  }
  const dropIdx = queue.findIndex(
    i => i.status === 'queued' && DROPPABLE_BEHAVIOR_TYPES.has(i.params.type)
  );
  if (dropIdx >= 0) {
    queue.splice(dropIdx, 1);
    resyncQueueIndexFromArray();
    recordDropped(1);
    return true;
  }
  return queue.length < limit;
}

function enqueueItem(item: QueueItem): void {
  pushQueueItem(item);
  recordEnqueueStorm();
  recordEventPhase(item.id, 'queued');
  recordQueueSize(getQueueLength());
  persistQueue();
}

export function enqueueEvent(
  params: SendEventParams,
  priority: QueuePriority = 'low',
  scheduleFlush?: (item: QueueItem) => void
): string {
  if (!shouldTrack()) return '';
  if (params.type === 'PAGE_VIEW') {
    void sendPageViewImmediately(params);
    return '';
  }

  const validation = validateEventParams(params);
  if (!validation.valid) return '';

  const validatedParams: SendEventParams = enrichCausality({
    ...params,
    eventData: validation.redactedData ?? params.eventData,
  });

  if (shouldDedupeEvent(validatedParams.type, validatedParams.eventData)) {
    recordDuplicated(1);
    return '';
  }

  try {
    const size = new TextEncoder().encode(
      JSON.stringify(validatedParams.eventData ?? {})
    ).length;
    if (size > MAX_EVENT_BYTES) {
      recordDropped(1);
      return '';
    }
  } catch {
    return '';
  }

  const now = Date.now();
  const type = eventTypeToQueueType(validatedParams.type);
  const sessionSequence = nextSessionSequence(validatedParams.sessionId);
  const item: QueueItem = {
    id: crypto.randomUUID(),
    type,
    params: { ...validatedParams },
    attempts: 0,
    priority,
    status: 'queued',
    createdAt: now,
    nextRetryAt: now,
    occurredAt: now,
    sessionSequence,
  };

  recordEnqueueForPressure();

  if (!enforceQueueLimits(item)) return '';

  if (
    item.params.type === 'BEHAVIOR_SUMMARY' ||
    item.params.type === 'HEARTBEAT'
  ) {
    const queue = getQueueMutable();
    const existingIdx = queue.findIndex(
      q =>
        q.params.type === item.params.type &&
        q.status === 'queued' &&
        q.params.sessionId === item.params.sessionId
    );
    if (existingIdx >= 0) {
      const existing = queue[existingIdx];
      item.id = existing.id;
      item.sessionSequence = existing.sessionSequence;
      item.createdAt = existing.createdAt;
      removeQueueItemById(existing.id);
    }
  }

  enqueueItem(item);
  if (item.type === 'conversion') {
    noteConversionEvent(item.params.sessionId, item.id);
  }
  if (scheduleFlush) {
    scheduleFlush(item);
  }
  return item.id;
}
